/**
 * `PackageTypeList` — the shared list for a single `package_type` facade
 * (bets / frames / principles), served by the unified `/api/packages` store
 * with a permanent `package_type=<type>` equality filter (CONTRACT_V2 §3.4).
 *
 * Built on the canonical shared `DataTable` / `PageHeader` / `SearchBox`
 * (design-system §6 — one DataTable, no per-list card chrome). Every row has a
 * `…` actions menu (View / Edit / Delete) and the header carries a Create
 * button + a link to the shared Package Graph. Create/Edit route to the unified
 * `packages` form so the facade pre-selects its type-specific columns.
 *
 * Bets/frames/principles all instantiate this with their own type + resource so
 * row links and the Create button stay on the facade path.
 */
import * as React from "react";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useTable } from "@refinedev/react-table";
import { useGo, useDelete, type HttpError } from "@refinedev/core";
import { MoreHorizontal, Pencil, Plus, Share2, Trash2 } from "@/components/ui/icon";

import type { Package, PackageType } from "@aos/shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StatusBadge } from "@/components/app/StatusBadge";
import { ConfirmDialog } from "@/components/app/ConfirmDialog";
import { formatDateTime } from "@/lib/format";
import { humanize } from "@/resources/projects/_form";
import {
  DataTable,
  PageHeader,
  SearchBox,
  type RefineTableCore,
} from "@/resources/projects/_shared";

export interface PackageTypeListProps {
  /**
   * Route base for this facade (e.g. "bets"). Drives list/create/edit/show
   * navigation paths. The data itself always reads from the unified `packages`
   * store (bets/frames/principles are not separate API resources).
   */
  resource: string;
  /** The `package_type` this typed view filters `/api/packages` to. */
  packageType: PackageType;
  /** Page title, e.g. "Bets". */
  title: string;
  /** One-line description shown under the title. */
  description: string;
  /** Empty-state message. */
  emptyMessage: string;
}

export function PackageTypeList({
  resource,
  packageType,
  title,
  description,
  emptyMessage,
}: PackageTypeListProps) {
  const go = useGo();
  const { mutate: deletePackage } = useDelete();
  const [pendingDelete, setPendingDelete] = React.useState<Package | null>(
    null,
  );

  const columns = useMemo<ColumnDef<Package, unknown>[]>(
    () => [
      { id: "name", accessorKey: "name", header: "Name" },
      {
        id: "target_level",
        accessorKey: "target_level",
        header: "Target Level",
        cell: ({ row }) => humanize(row.original.target_level),
      },
      {
        id: "domain",
        accessorKey: "domain",
        header: "Domain",
        cell: ({ row }) => row.original.domain ?? "—",
      },
      {
        id: "maturity_stage",
        accessorKey: "maturity_stage",
        header: "Maturity",
        cell: ({ row }) => (
          <Badge variant="secondary">
            {humanize(row.original.maturity_stage)}
          </Badge>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "updated_at",
        accessorKey: "updated_at",
        header: "Updated",
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {formatDateTime(row.original.updated_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onShow={() => go({ to: `/${resource}/${row.original.id}` })}
            onEdit={() => go({ to: `/${resource}/${row.original.id}/edit` })}
            onDelete={() => setPendingDelete(row.original)}
          />
        ),
      },
    ],
    [go, resource],
  );

  const table = useTable<Package, HttpError>({
    columns,
    refineCoreProps: {
      resource: "packages",
      pagination: { pageSize: 25 },
      sorters: { initial: [{ field: "updated_at", order: "desc" }] },
      filters: {
        permanent: [
          { field: "package_type", operator: "eq", value: packageType },
        ],
      },
    },
  });

  const core = table.refineCore as unknown as RefineTableCore & {
    setFilters: (
      filters: { field: string; operator: "contains"; value: string }[],
    ) => void;
  };
  const [search, setSearch] = React.useState("");

  React.useEffect(() => {
    const handle = setTimeout(() => {
      core.setFilters([{ field: "q", operator: "contains", value: search }]);
    }, 300);
    return () => clearTimeout(handle);
  }, [search, core]);

  return (
    <div className="space-y-6">
      <PageHeader
        title={title}
        description={description}
        action={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => go({ to: "/package-graph" })}
            >
              <Share2 className="mr-2 h-4 w-4" />
              Package Graph
            </Button>
            <Button onClick={() => go({ to: `/${resource}/create` })}>
              <Plus className="mr-2 h-4 w-4" /> New {title.replace(/s$/, "")}
            </Button>
          </div>
        }
      />

      <SearchBox
        value={search}
        onChange={setSearch}
        placeholder={`Search ${title.toLowerCase()}…`}
      />

      <DataTable
        table={table}
        core={core}
        columns={columns}
        emptyMessage={emptyMessage}
        onRowClick={(p) => go({ to: `/${resource}/${p.id}` })}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Delete ${title.replace(/s$/, "").toLowerCase()}?`}
        description={
          <>
            This removes{" "}
            <span className="font-medium">{pendingDelete?.name}</span> from the
            store. This action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        onConfirm={() =>
          new Promise<void>((resolve) => {
            if (!pendingDelete) return resolve();
            deletePackage(
              { resource: "packages", id: pendingDelete.id },
              { onSuccess: () => resolve(), onError: () => resolve() },
            );
          })
        }
      />
    </div>
  );
}

function RowActions({
  onShow,
  onEdit,
  onDelete,
}: {
  onShow: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Open actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onShow}>View</DropdownMenuItem>
        <DropdownMenuItem onClick={onEdit}>
          <Pencil className="mr-2 h-4 w-4" /> Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="mr-2 h-4 w-4" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default PackageTypeList;
